import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X, Plus, Trash2 } from 'lucide-react';
import { SECTION_TEMPLATES } from './sectionTemplates';

const ICON_OPTIONS = ['Shield', 'DollarSign', 'Star', 'Truck', 'MapPin', 'Phone', 'Mail', 'Clock', 'Cog', 'Award', 'Heart', 'Zap', 'Target', 'Users'];

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function HeroEditor({ props, onUpdate }) {
  const Toggle = ({ k, label }) => (
    <label className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 rounded">
      <span className="text-xs text-gray-700">{label}</span>
      <input
        type="checkbox"
        checked={props[k] !== false}
        onChange={e => onUpdate({ [k]: e.target.checked })}
        className="accent-[#E31937] h-4 w-4"
        data-testid={`hero-toggle-${k}`}
      />
    </label>
  );
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Subheading"><Textarea value={props.subheading || ''} onChange={e => onUpdate({ subheading: e.target.value })} rows={2} className="text-sm" /></Field>
      <Field label="Background Image URL"><Input value={props.backgroundImage || ''} onChange={e => onUpdate({ backgroundImage: e.target.value })} className="h-8 text-sm" placeholder="https://..." /></Field>
      <Field label="Primary Button Text"><Input value={props.ctaText || ''} onChange={e => onUpdate({ ctaText: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Primary Button Link"><Input value={props.ctaLink || ''} onChange={e => onUpdate({ ctaLink: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Secondary Button Text"><Input value={props.ctaSecondaryText || ''} onChange={e => onUpdate({ ctaSecondaryText: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Secondary Button Link"><Input value={props.ctaSecondaryLink || ''} onChange={e => onUpdate({ ctaSecondaryLink: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Visibility (Public Site)">
        <div className="space-y-1.5">
          <Toggle k="showHeading" label="Show Heading" />
          <Toggle k="showSubheading" label="Show Subheading" />
          <Toggle k="showCtaPrimary" label="Show Primary Button" />
          <Toggle k="showCtaSecondary" label="Show Secondary Button" />
        </div>
      </Field>
      <Field label="Compact">
        <Select value={props.compact ? 'yes' : 'no'} onValueChange={v => onUpdate({ compact: v === 'yes' })}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">Full Height</SelectItem>
            <SelectItem value="yes">Compact</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function StatsEditor({ props, onUpdate }) {
  const items = props.items || [];
  const updateItem = (idx, field, val) => { const copy = [...items]; copy[idx] = { ...copy[idx], [field]: val }; onUpdate({ items: copy }); };
  const addItem = () => onUpdate({ items: [...items, { num: '0', label: 'Label' }] });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });
  return (
    <div className="space-y-3">
      <label className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 rounded">
        <span className="text-xs text-gray-700 font-semibold">Show Homepage Stats Section</span>
        <input
          type="checkbox"
          checked={props.enabled === true}
          onChange={e => onUpdate({ enabled: e.target.checked })}
          className="accent-[#E31937] h-4 w-4"
          data-testid="stats-toggle-enabled"
        />
      </label>
      <p className="text-[10px] text-gray-400">OFF by default. Stats are hidden from public site unless toggled ON.</p>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1 space-y-1">
            <Input value={item.num} onChange={e => updateItem(i, 'num', e.target.value)} placeholder="Number" className="h-7 text-sm" />
            <Input value={item.label} onChange={e => updateItem(i, 'label', e.target.value)} placeholder="Label" className="h-7 text-sm" />
          </div>
          <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:bg-red-50 rounded mt-1"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Add Stat</Button>
    </div>
  );
}

function FeaturesEditor({ props, onUpdate }) {
  const items = props.items || [];
  const updateItem = (idx, field, val) => { const copy = [...items]; copy[idx] = { ...copy[idx], [field]: val }; onUpdate({ items: copy }); };
  const addItem = () => onUpdate({ items: [...items, { icon: 'Star', title: 'New Feature', description: 'Description' }] });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });
  return (
    <div className="space-y-3">
      <Field label="Section Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Feature Items</p>
      {items.map((item, i) => (
        <div key={i} className="bg-gray-50 rounded p-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400">Item {i + 1}</span>
            <button onClick={() => removeItem(i)} className="text-red-400 hover:bg-red-50 rounded p-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
          <Select value={item.icon} onValueChange={v => updateItem(i, 'icon', v)}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{ICON_OPTIONS.map(ic => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={item.title} onChange={e => updateItem(i, 'title', e.target.value)} placeholder="Title" className="h-7 text-sm" />
          <Textarea value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Description" rows={2} className="text-sm" />
        </div>
      ))}
      <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Add Feature</Button>
    </div>
  );
}

function CTAEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Subheading"><Textarea value={props.subheading || ''} onChange={e => onUpdate({ subheading: e.target.value })} rows={2} className="text-sm" /></Field>
      <Field label="Button Text"><Input value={props.ctaText || ''} onChange={e => onUpdate({ ctaText: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Button Link"><Input value={props.ctaLink || ''} onChange={e => onUpdate({ ctaLink: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Background Image URL"><Input value={props.backgroundImage || ''} onChange={e => onUpdate({ backgroundImage: e.target.value })} className="h-8 text-sm" placeholder="https://..." /></Field>
    </div>
  );
}

function TextBlockEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Content"><Textarea value={props.content || ''} onChange={e => onUpdate({ content: e.target.value })} rows={6} className="text-sm" /></Field>
      <Field label="Alignment">
        <Select value={props.alignment || 'left'} onValueChange={v => onUpdate({ alignment: v })}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function TextImageEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Content"><Textarea value={props.content || ''} onChange={e => onUpdate({ content: e.target.value })} rows={5} className="text-sm" /></Field>
      <Field label="Image URL"><Input value={props.image || ''} onChange={e => onUpdate({ image: e.target.value })} className="h-8 text-sm" placeholder="https://..." /></Field>
      <Field label="Layout">
        <Select value={props.layout || 'text-left'} onValueChange={v => onUpdate({ layout: v })}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="text-left">Text Left, Image Right</SelectItem>
            <SelectItem value="text-right">Image Left, Text Right</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function TestimonialsEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Subheading"><Input value={props.subheading || ''} onChange={e => onUpdate({ subheading: e.target.value })} className="h-8 text-sm" /></Field>
      <p className="text-xs text-gray-400">Testimonials are auto-populated from the CMS Testimonials section.</p>
    </div>
  );
}

function FeaturedInventoryEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><Input value={props.heading || ''} onChange={e => onUpdate({ heading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Subheading"><Input value={props.subheading || ''} onChange={e => onUpdate({ subheading: e.target.value })} className="h-8 text-sm" /></Field>
      <Field label="Link Text"><Input value={props.linkText || ''} onChange={e => onUpdate({ linkText: e.target.value })} className="h-8 text-sm" /></Field>
      <p className="text-xs text-gray-400">Featured inventory is auto-populated from units marked as "Featured".</p>
    </div>
  );
}

function ContactCardsEditor({ props, onUpdate }) {
  const items = props.items || [];
  const updateItem = (idx, field, val) => { const copy = [...items]; copy[idx] = { ...copy[idx], [field]: val }; onUpdate({ items: copy }); };
  const addItem = () => onUpdate({ items: [...items, { icon: 'MapPin', title: 'Title', text: 'Detail' }] });
  const removeItem = (idx) => onUpdate({ items: items.filter((_, i) => i !== idx) });
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="bg-gray-50 rounded p-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400">Card {i + 1}</span>
            <button onClick={() => removeItem(i)} className="text-red-400 p-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
          <Select value={item.icon} onValueChange={v => updateItem(i, 'icon', v)}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{ICON_OPTIONS.map(ic => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={item.title} onChange={e => updateItem(i, 'title', e.target.value)} placeholder="Title" className="h-7 text-sm" />
          <Input value={item.text} onChange={e => updateItem(i, 'text', e.target.value)} placeholder="Detail" className="h-7 text-sm" />
        </div>
      ))}
      <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Add Card</Button>
    </div>
  );
}

function SpacerEditor({ props, onUpdate }) {
  return (
    <div className="space-y-3">
      <Field label="Height (px)"><Input type="number" value={props.height || 60} onChange={e => onUpdate({ height: parseInt(e.target.value) || 60 })} className="h-8 text-sm" /></Field>
    </div>
  );
}

const EDITORS = {
  hero: HeroEditor,
  stats: StatsEditor,
  features: FeaturesEditor,
  cta: CTAEditor,
  text_block: TextBlockEditor,
  text_image: TextImageEditor,
  testimonials: TestimonialsEditor,
  featured_inventory: FeaturedInventoryEditor,
  contact_cards: ContactCardsEditor,
  spacer: SpacerEditor,
};

export function SectionEditor({ section, onUpdate, onClose }) {
  if (!section) return null;
  const Editor = EDITORS[section.type];
  const template = SECTION_TEMPLATES[section.type];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{template?.label || section.type}</h3>
          <p className="text-[10px] text-gray-400">Double-click text on canvas to edit inline</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4 text-gray-500" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {Editor ? <Editor props={section.props || {}} onUpdate={onUpdate} /> : <p className="text-sm text-gray-400">No editor available for this section type.</p>}
      </div>
    </div>
  );
}
